import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Tilt from 'react-parallax-tilt';
import { CheckIcon, CloudArrowUpIcon, CpuChipIcon, DocumentCheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { createCheckoutSession } from '../api/stripe';
import SEO from '../components/SEO';

function PricingCard({ title, price, features, popular, index, planId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (price === 'Custom') {
      window.location.href = '/contact';  
      return;
    }

    if (!user) {
      toast.error('Please log in to subscribe');
      navigate('/signin', { 
        state: { 
          from: '/pricing',
          message: 'Please log in to subscribe to a plan' 
        } 
      });
      return;
    }

    try {
      setLoading(true);
      const response = await createCheckoutSession(user.id, planId);
      
      // The backend returns { url: session.url }
      if (!response.url) {
        throw new Error('Invalid checkout session response');
      }
      
      window.location.href = response.url;
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to start subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="w-full h-full"
    >
      <Tilt tiltMaxAngleX={10} tiltMaxAngleY={10} scale={1.05} className="h-full">
        <div className={`glass p-8 rounded-modern text-center hover:bg-white/5 transition-all duration-300 h-full flex flex-col ${popular ? 'border-2 border-[#4F46E5]' : ''}`}>
          {popular && (
            <div className="bg-gradient-to-br from-[#2563EB] to-[#4F46E5] text-white px-4 py-1 rounded-full text-sm font-medium absolute -top-3 left-1/2 transform -translate-x-1/2">
              Most Popular
            </div>
          )}
          <h3 className="text-2xl font-bold mb-4">{title}</h3>
          <div className="mb-6">
            <span className="text-4xl font-bold">${price}</span>
            {price !== 'Custom' && <span className="text-gray-400">/month</span>}
          </div>
          <ul className="text-left space-y-4 mb-8 flex-grow">
            {features.map((feature, i) => (
              <li key={i} className="flex items-center">
                <CheckIcon className="h-5 w-5 text-[#4F46E5] mr-2" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={handleSubscribe}
            disabled={loading}
            className={`w-full py-3 px-6 rounded-modern font-medium transition-all duration-300 ${
              popular
                ? 'bg-gradient-to-br from-[#2563EB] to-[#4F46E5] text-white hover:from-[#1D4ED8] hover:to-[#4338CA]'
                : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            {loading ? 'Processing...' : price === 'Custom' ? 'Contact Sales' : user ? 'Subscribe Now' : 'Login to Subscribe'}
          </button>
        </div>
      </Tilt>
    </motion.div>
  );
}

function FeatureCard({ icon: Icon, title, description }) {
  return (
    <div className="glass p-6 rounded-modern">
      <div className="p-4 bg-gradient-to-br from-[#2563EB] to-[#4F46E5] rounded-full w-16 h-16 mx-auto mb-6 glow">
        <Icon className="w-8 h-8 text-white" />
      </div>
      <h3 className="text-xl font-bold text-white">{title}</h3>
      <p className="text-white/80">{description}</p>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="glass rounded-modern overflow-hidden">
      <button
        className="w-full px-6 py-4 text-left flex justify-between items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-lg font-medium text-white">{question}</span>
        <span className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="px-6 pb-4">
          <p className="text-white/80">{answer}</p>
        </div>
      )}
    </div>
  );
}

function Pricing() {
  const plans = [
    {
      title: "Pro",
      price: "29",
      planId: process.env.REACT_APP_STRIPE_PRO_PLAN_ID,
      popular: true,
      features: [
        "1,000 images per month",
        "Max file size: 20MB",
        "Priority processing",
        "RESTful API access",
        "5,000 API calls/month",
        "API key management",
        "Technical support",
        "Usage analytics",
        "Detailed API documentation",
        "Code examples in multiple languages",
        "99.9% uptime SLA"
      ]
    },
    {
      title: "Business",
      price: "99",
      planId: process.env.REACT_APP_STRIPE_BUSINESS_PLAN_ID,
      popular: false,
      features: [
        "10,000 images per month",
        "Max file size: 50MB",
        "Priority processing",
        "RESTful API access",
        "25,000 API calls/month",
        "Multiple API keys",
        "Priority support",
        "Advanced analytics",
        "Bulk processing",
        "Custom integration support",
        "Webhook notifications",
        "99.99% uptime SLA"
      ]
    },
    {
      title: "Enterprise",
      price: "Custom",
      planId: null,
      popular: false,
      features: [
        "Unlimited images",
        "Max file size: 100MB",
        "Highest priority processing",
        "RESTful API access",
        "Custom API limits",
        "Multiple API keys",
        "24/7 support",
        "Advanced analytics",
        "Bulk processing",
        "Custom integration",
        "Dedicated account manager",
        "Custom SLA guarantee",
        "On-premise deployment options"
      ]
    }
  ];

  const faqs = [
    {
      question: "How does the API work?",
      answer: "Our RESTful API allows you to programmatically remove backgrounds from images. After subscribing, you'll get an API key and immediate access to our comprehensive documentation with examples in multiple programming languages."
    },
    {
      question: "What's the API response time?",
      answer: "Average response time is under 3 seconds. Pro and Business plans get priority processing with even faster response times. Enterprise plans include custom SLA guarantees."
    },
    {
      question: "Can I try the API before subscribing?",
      answer: "Yes! You can test our web interface for free. Once you're ready to integrate with your application, choose a plan that matches your needs. All plans come with a 30-day money-back guarantee."
    },
    {
      question: "Do you offer technical support?",
      answer: "Yes, all paid plans include technical support. Business plans get priority support, and Enterprise plans include 24/7 support with a dedicated account manager."
    }
  ];

  return (
    <>
      <SEO 
        title="Pricing Plans | Remuvr Background Removal Tool"
        description="Flexible pricing plans for all your background removal needs. Start with our free plan, upgrade to Pro for unlimited processing, or choose Business for team collaboration. Enterprise solutions available. 100% satisfaction guaranteed."
        url="https://remuvr.com/pricing"
        pageName="pricing"
        price="29.99"
        reviewCount="150"
        rating="4.9"
        type="website"
      />
      <div className="min-h-screen bg-[#0a1930]">
        <div className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Choose the plan that's right for you. All plans include our core features.
              Upgrade, downgrade, or cancel anytime.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto px-4">
            {plans.map((plan, index) => (
              <div key={index} className="h-full flex">
                <PricingCard {...plan} index={index} />
              </div>
            ))}
          </div>

          {/* Features */}
          <section className="py-32">
            <div className="container mx-auto px-4">
              <h2 className="text-4xl font-bold text-center mb-16 text-white">
                All Plans Include
              </h2>
              <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
                <FeatureCard
                  icon={CloudArrowUpIcon}
                  title="Cloud Processing"
                  description="Process your images in the cloud with our powerful AI"
                />
                <FeatureCard
                  icon={CpuChipIcon}
                  title="AI Technology"
                  description="Advanced machine learning for accurate results"
                />
                <FeatureCard
                  icon={DocumentCheckIcon}
                  title="Quality Guarantee"
                  description="100% satisfaction guarantee or your money back"
                />
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-32">
            <div className="container mx-auto px-4">
              <h2 className="text-4xl font-bold text-center mb-16 text-white">
                Frequently Asked Questions
              </h2>
              <div className="max-w-3xl mx-auto space-y-8">
                {faqs.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export default Pricing;
